import React, { useState, useContext, useEffect } from "react";
import Select from "react-select";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormText,
  CustomInput,
  Button,
} from "reactstrap";
import { StateContext } from "../../utils/context";
import ProductPlanPreview from "./productPlanPreview";

export const EMPTY_PRODUCT = {
  name: "",
  description: "",
  features: [],
  amount: "",
  interval: "month",
  deal_percent: "",
  song_limit: "",
  artist_limit: "",
  music_link_limit: "",
  link_landing_page_limit: "",
  music_link_enabled: false,
  link_landing_page_enabled: false,
};

const BILLING_INTERVAL_OPTIONS = [
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

const ProductFields = ({ draftProduct, setDraftProduct, tenantCurrency = "eur", disabled = false }) => {
  const { currentUser } = useContext(StateContext);
  const [newFeature, setNewFeature] = useState("");
  const musicLinkAvailable = !!currentUser?.music_link;
  const linkLandingPageAvailable = !!currentUser?.link_in_bio;
  const hasOptionalModules = musicLinkAvailable || linkLandingPageAvailable;
  const selectedInterval = BILLING_INTERVAL_OPTIONS.find(
    (option) => option.value === draftProduct.interval
  ) || BILLING_INTERVAL_OPTIONS[0];

  const currencyLabel = (tenantCurrency || "eur").toUpperCase();
  const features = draftProduct.features || [];

  const addFeature = () => {
    const trimmed = newFeature.trim();
    if (!trimmed) return;
    setDraftProduct({
      ...draftProduct,
      features: [...features, trimmed],
    });
    setNewFeature("");
  };

  const removeFeature = (index) => {
    setDraftProduct({
      ...draftProduct,
      features: features.filter((_, i) => i !== index),
    });
  };

  const handleFeatureKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFeature();
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    setDraftProduct((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!musicLinkAvailable && (prev.music_link_enabled || prev.music_link_limit)) {
        next.music_link_enabled = false;
        next.music_link_limit = "";
        changed = true;
      }
      if (!linkLandingPageAvailable && (prev.link_landing_page_enabled || prev.link_landing_page_limit)) {
        next.link_landing_page_enabled = false;
        next.link_landing_page_limit = "";
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [currentUser, musicLinkAvailable, linkLandingPageAvailable, setDraftProduct]);

  return (
    <Row>
      <Col lg={8}>
        <div className="stripe-form-section">
          <h6 className="stripe-subsection-title">Plan Details</h6>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Plan Name</Label>
                <Input
                  value={draftProduct.name}
                  onChange={(e) => setDraftProduct({ ...draftProduct, name: e.target.value })}
                  disabled={disabled}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Billing Interval</Label>
                <Select
                  components={{ IndicatorSeparator: () => null }}
                  options={BILLING_INTERVAL_OPTIONS}
                  value={selectedInterval}
                  onChange={(option) => setDraftProduct({
                    ...draftProduct,
                    interval: option?.value || "month",
                  })}
                  isDisabled={disabled}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Price</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draftProduct.amount}
                  onChange={(e) => setDraftProduct({ ...draftProduct, amount: e.target.value })}
                  disabled={disabled}
                />
                <FormText color="muted">
                  If you want to change the price, please do it here so your sub-users have
                  the correct data visible in the system. Prices updated directly in Stripe
                  are not synced.
                </FormText>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Currency</Label>
                <Input value={currencyLabel} disabled />
                <FormText color="muted">
                  Configured in{" "}
                  <a href="/subscription-config">Subscription Configuration</a>
                </FormText>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Subuser Deal (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={draftProduct.deal_percent}
                  onChange={(e) => setDraftProduct({ ...draftProduct, deal_percent: e.target.value })}
                  disabled={disabled}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mb-0">
            <Label>Description</Label>
            <Input
              type="textarea"
              rows={3}
              required
              value={draftProduct.description}
              onChange={(e) => setDraftProduct({ ...draftProduct, description: e.target.value })}
              disabled={disabled}
            />
            <FormText color="muted">Required. Write a short description of the plan. This will be shown on the customer plan.</FormText>
          </FormGroup>
        </div>

        <div className="stripe-form-section">
          <h6 className="stripe-subsection-title">Features</h6>
          <FormText color="muted" className="d-block mb-3">
            Add features and unique selling points for the product. These appear on the pricing plans page.
          </FormText>
          <div className="stripe-feature-add-row mb-3">
            <Input
              className="stripe-feature-input"
              value={newFeature}
              placeholder="e.g. Unlimited uploads"
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyDown={handleFeatureKeyDown}
              disabled={disabled}
            />
            <Button
              color="success"
              type="button"
              className="stripe-feature-add-btn"
              onClick={addFeature}
              disabled={disabled || !newFeature.trim()}
            >
              Add
            </Button>
          </div>
          {features.length > 0 ? (
            <ul className="stripe-feature-list">
              {features.map((feature, index) => (
                <li key={`feature-${index}`} className="stripe-feature-item">
                  <span className="stripe-feature-text">{feature}</span>
                  <Button
                    color="link"
                    className="stripe-feature-remove p-0"
                    type="button"
                    onClick={() => removeFeature(index)}
                    aria-label={`Remove feature ${feature}`}
                    disabled={disabled}
                  >
                    <i className="fas fa-times" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="stripe-feature-empty text-muted mb-0">No features added yet.</p>
          )}
        </div>

        {hasOptionalModules && (
        <div className="stripe-form-section">
          <h6 className="stripe-subsection-title">Modules</h6>
          <Row>
            {musicLinkAvailable && (
            <Col md={linkLandingPageAvailable ? 6 : 12}>
              <div className={`stripe-entitlement-option ${draftProduct.music_link_enabled ? "is-enabled" : ""}`}>
                <CustomInput
                  type="checkbox"
                  id="music_link_enabled"
                  name="music_link_enabled"
                  label="Music Link"
                  checked={!!draftProduct.music_link_enabled}
                  onChange={(e) => setDraftProduct({
                    ...draftProduct,
                    music_link_enabled: e.target.checked,
                  })}
                  disabled={disabled}
                />
                <p className="stripe-entitlement-hint text-muted mb-0">
                  Let subscribers create shareable music links.
                </p>
              </div>
            </Col>
            )}
            {linkLandingPageAvailable && (
            <Col md={musicLinkAvailable ? 6 : 12}>
              <div className={`stripe-entitlement-option ${draftProduct.link_landing_page_enabled ? "is-enabled" : ""}`}>
                <CustomInput
                  type="checkbox"
                  id="link_landing_page_enabled"
                  name="link_landing_page_enabled"
                  label="Link Landing Page"
                  checked={!!draftProduct.link_landing_page_enabled}
                  onChange={(e) => setDraftProduct({
                    ...draftProduct,
                    link_landing_page_enabled: e.target.checked,
                  })}
                  disabled={disabled}
                />
                <p className="stripe-entitlement-hint text-muted mb-0">
                  Let subscribers build custom link landing pages.
                </p>
              </div>
            </Col>
            )}
          </Row>
        </div>
        )}

        <div className="stripe-form-section">
          <h6 className="stripe-subsection-title">Allowances</h6>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Number of Songs</Label>
                <Input
                  type="number"
                  min="0"
                  value={draftProduct.song_limit}
                  onChange={(e) => setDraftProduct({ ...draftProduct, song_limit: e.target.value })}
                  disabled={disabled}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Number of Artists</Label>
                <Input
                  type="number"
                  min="0"
                  value={draftProduct.artist_limit}
                  onChange={(e) => setDraftProduct({ ...draftProduct, artist_limit: e.target.value })}
                  disabled={disabled}
                />
              </FormGroup>
            </Col>
          </Row>

          {(musicLinkAvailable || linkLandingPageAvailable) && (
          <Row>
            {musicLinkAvailable && (
            <Col md={linkLandingPageAvailable ? 6 : 12}>
              <FormGroup className="mb-0">
                <Label>Number of Music Links</Label>
                <Input
                  type="number"
                  min="0"
                  value={draftProduct.music_link_limit}
                  onChange={(e) => setDraftProduct({ ...draftProduct, music_link_limit: e.target.value })}
                  disabled={disabled || !draftProduct.music_link_enabled}
                />
              </FormGroup>
            </Col>
            )}
            {linkLandingPageAvailable && (
            <Col md={musicLinkAvailable ? 6 : 12}>
              <FormGroup className="mb-0">
                <Label>Number of Link Landing Pages</Label>
                <Input
                  type="number"
                  min="0"
                  value={draftProduct.link_landing_page_limit}
                  onChange={(e) => setDraftProduct({ ...draftProduct, link_landing_page_limit: e.target.value })}
                  disabled={disabled || !draftProduct.link_landing_page_enabled}
                />
              </FormGroup>
            </Col>
            )}
          </Row>
          )}
        </div>
      </Col>

      <Col lg={4}>
        <div className="stripe-product-preview-panel">
          <p className="stripe-product-preview-label">Customer preview</p>
          <FormText color="muted" className="d-block mb-3">
            Live preview of how this plan appears to subscribers.
          </FormText>
          <ProductPlanPreview
            draftProduct={draftProduct}
            tenantCurrency={tenantCurrency}
          />
        </div>
      </Col>
    </Row>
  );
};

const toDraftString = (value) => (
  value === 0 || value ? String(value) : ""
);

export const productToDraft = (product, currentUser) => ({
  name: product.name || "",
  description: product.description || "",
  features: product.features || [],
  amount: toDraftString(product.amount),
  interval: product.interval || "month",
  deal_percent: toDraftString(product.deal_percent),
  song_limit: toDraftString(product.song_limit),
  artist_limit: toDraftString(product.artist_limit),
  music_link_limit: currentUser?.music_link ? toDraftString(product.music_link_limit) : "",
  link_landing_page_limit: currentUser?.link_in_bio ? toDraftString(product.link_landing_page_limit) : "",
  music_link_enabled: !!currentUser?.music_link && !!product.music_link_enabled,
  link_landing_page_enabled: !!currentUser?.link_in_bio && !!product.link_landing_page_enabled,
});

export const draftToPayload = (draftProduct, currentUser) => {
  const features = (draftProduct.features || [])
    .map((f) => f.trim())
    .filter(Boolean);

  const musicLinkEnabled = !!currentUser?.music_link && !!draftProduct.music_link_enabled;
  const linkLandingPageEnabled = !!currentUser?.link_in_bio && !!draftProduct.link_landing_page_enabled;

  return {
    name: draftProduct.name,
    description: (draftProduct.description || "").trim(),
    features,
    amount: draftProduct.amount,
    interval: draftProduct.interval,
    deal_percent: draftProduct.deal_percent || 0,
    song_limit: draftProduct.song_limit || 0,
    artist_limit: draftProduct.artist_limit || 0,
    music_link_limit: musicLinkEnabled ? (draftProduct.music_link_limit || 0) : 0,
    link_landing_page_limit: linkLandingPageEnabled ? (draftProduct.link_landing_page_limit || 0) : 0,
    music_link_enabled: musicLinkEnabled,
    link_landing_page_enabled: linkLandingPageEnabled,
    active: true,
  };
};

export default ProductFields;
